/**
 * Settings → Integrations: Email Inbox (Microsoft 365) — sales rework Phase 7.
 * The CRM's shared mailbox (feedback@ids-dairy.com) read via the Graph API.
 * The client secret is write-only: the server returns only a set-flag + hint.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

function M365Settings() {
  const { authFetch, user } = useAuth();
  const canEdit = user?.role === 'admin';

  const [form, setForm] = useState({
    enabled: false, tenant_id: '', client_id: '', mailbox: 'feedback@ids-dairy.com',
    client_secret_set: false, client_secret_hint: null, last_polled_at: null, last_result: null
  });
  const [secretInput, setSecretInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/admin/support-settings/m365');
      if (!response.ok) return;
      const data = await response.json();
      setForm(data.data);
    } catch (err) {
      setMessage('Failed to load M365 settings');
    }
  }, [authFetch]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      const body = {
        enabled: form.enabled,
        tenant_id: form.tenant_id,
        client_id: form.client_id,
        mailbox: form.mailbox
      };
      if (secretInput.trim()) body.client_secret = secretInput.trim();
      const response = await authFetch('/api/v1/admin/support-settings/m365', {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save');
      setForm(data.data);
      setSecretInput('');
      setMessage('Email inbox settings saved');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="integration-card">
      <h2>Email Inbox (Microsoft 365)</h2>
      <p className="section-desc">
        The CRM's customer-visible mailbox — CC'd on outbound drafts and polled every 2 minutes
        for captured mail (Inbox screen). Uses an Azure app registration with application
        permission <code>Mail.ReadWrite</code> scoped to the shared mailbox.
      </p>
      {message && <div className="settings-message">{message}</div>}

      <div className="form-row compact">
        <div className="form-group">
          <label className="checkbox-label config-checkbox">
            <input
              type="checkbox"
              checked={form.enabled}
              disabled={!canEdit}
              onChange={(e) => setForm(prev => ({ ...prev, enabled: e.target.checked }))}
            />
            Enabled
          </label>
        </div>
        <div className="form-group">
          <label>Mailbox address</label>
          <input
            value={form.mailbox}
            disabled={!canEdit}
            onChange={(e) => setForm(prev => ({ ...prev, mailbox: e.target.value }))}
          />
        </div>
      </div>
      <div className="form-row compact">
        <div className="form-group">
          <label>Tenant ID</label>
          <input
            value={form.tenant_id}
            disabled={!canEdit}
            onChange={(e) => setForm(prev => ({ ...prev, tenant_id: e.target.value }))}
          />
        </div>
        <div className="form-group">
          <label>Client ID</label>
          <input
            value={form.client_id}
            disabled={!canEdit}
            onChange={(e) => setForm(prev => ({ ...prev, client_id: e.target.value }))}
          />
        </div>
        <div className="form-group">
          <label>
            Client secret {form.client_secret_set ? `(stored ${form.client_secret_hint})` : '(not set)'}
          </label>
          <input
            type="password"
            value={secretInput}
            placeholder="Leave blank to keep current secret"
            disabled={!canEdit}
            onChange={(e) => setSecretInput(e.target.value)}
          />
        </div>
      </div>
      <p className="field-hint">
        Last poll: {form.last_polled_at ? new Date(form.last_polled_at).toLocaleString('en-GB') : 'never'}
        {form.last_result ? ` — ${form.last_result}` : ''}
      </p>
      {canEdit && (
        <button className="btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  );
}

export default M365Settings;
